<script lang="ts" setup>
  import {
    AUTOCOMPLETE_DEFAULT_MIN_CHARS,
    Autocomplete,
    type AutocompleteOption,
    type FieldSizeType,
  } from '@tundr/ds-components';
  import { useLocale } from '@tundr/i18n';
  import { useField } from 'vee-validate';
  import { computed, ref, toRef } from 'vue';
  import googleMapsLogoUrl from './assets/google-maps-logo.svg?url';
  import { formatAddress, formatStreetLine } from './format-address.ts';
  import type { ParsedAddress } from './places.types.ts';
  import { usePlacesAutocomplete } from './usePlacesAutocomplete.ts';

  type AddressAutocompleteFieldProps = {
    name: string;
    id?: string;
    testId?: string;
    label?: string;
    ariaLabel?: string;
    optionalLabel?: boolean;
    placeholder?: string;
    helperText?: string;
    error?: string;
    warning?: string;
    disabled?: boolean;
    required?: boolean;
    size?: FieldSizeType;
    minChars?: number;
    rules?: (value: string | null) => string | boolean;
    formatSelection?: (address: ParsedAddress) => string;
  };

  const props = withDefaults(defineProps<AddressAutocompleteFieldProps>(), {
    minChars: AUTOCOMPLETE_DEFAULT_MIN_CHARS,
  });

  const emit = defineEmits<{
    select: [address: ParsedAddress];
    search: [query: string];
    error: [reason: unknown];
    blur: [event: FocusEvent];
  }>();

  const { t } = useLocale('geo');

  const partialStreet = ref<string | null>(null);
  let selectionId = 0;

  const { suggestions, loading, error, preload, search, resolve } =
    usePlacesAutocomplete({
      minChars: props.minChars,
      onError: (reason) => emit('error', reason),
    });

  const serviceError = computed(() => error.value !== null);

  const {
    value: fieldValue,
    errorMessage,
    handleChange,
    handleBlur,
    meta,
  } = useField<string | null>(() => props.name, props.rules, {
    label: toRef(props, 'label'),
  });

  const options = computed<AutocompleteOption[]>(() =>
    suggestions.value.map((suggestion) => ({
      value: suggestion.placeId,
      label: suggestion.mainText,
      description: suggestion.secondaryText,
    })),
  );

  const displayError = computed(() => {
    if (props.error) return props.error;
    return meta.touched ? errorMessage.value : undefined;
  });

  const warning = computed(() => {
    if (serviceError.value) return t('address_autocomplete.unavailable');
    if (partialStreet.value) {
      return t('address_autocomplete.missing_street_number');
    }
    return props.warning;
  });

  const defaultFormatSelection = (address: ParsedAddress) =>
    formatAddress({
      street: formatStreetLine(address),
      postalCode: address.postalCode,
      city: address.city,
      provinceCode: address.provinceCode,
    });

  const onBlur = (event: FocusEvent) => {
    handleBlur(event);
    emit('blur', event);
  };

  const onSearch = (query: string) => {
    selectionId += 1;
    if (partialStreet.value && !query.startsWith(partialStreet.value)) {
      partialStreet.value = null;
    }
    search(query);
    emit('search', query);
  };

  const onSelect = async (option: AutocompleteOption) => {
    selectionId += 1;
    const currentSelection = selectionId;
    let address: ParsedAddress;
    try {
      address = await resolve(option.value);
    } catch {
      return;
    }
    if (currentSelection !== selectionId) return;

    if (address.streetNumber) {
      partialStreet.value = null;
      handleChange((props.formatSelection ?? defaultFormatSelection)(address));
    } else {
      const partial = `${address.street}, `;
      partialStreet.value = partial;
      handleChange(partial);
      search(partial);
    }
    emit('select', address);
  };
</script>

<template>
  <Autocomplete
    :id="id"
    :test-id="testId"
    :name="name"
    :label="label"
    :aria-label="ariaLabel"
    :optional-label="optionalLabel"
    :placeholder="placeholder"
    :helper-text="helperText"
    :error="displayError"
    :warning="warning"
    :disabled="disabled"
    :required="required"
    :size="size"
    :min-chars="minChars"
    :options="options"
    :loading="loading"
    :no-results-message="t('address_autocomplete.no_results')"
    :model-value="fieldValue ?? null"
    :open="serviceError ? false : undefined"
    @update:model-value="handleChange"
    @search="onSearch"
    @select="onSelect"
    @focus="preload"
    @blur="onBlur"
  >
    <template #footer>
      <img :src="googleMapsLogoUrl" alt="Google Maps" translate="no">
    </template>
  </Autocomplete>
</template>
